import { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { Category } from "../../../redux/types/categoryTypes";
import type { SellingValues } from "./types";
import { validateField } from "./validation";
import { parsePriceCents } from "./pricing";

interface FieldProps {
  name: keyof SellingValues;
  label: string;
  categories?: Category[];
  required?: boolean;
  helper?: string;
  placeholder?: string;
  maxLength?: number;
  multiline?: boolean;
  numeric?: boolean;
  money?: boolean;
  disabled?: boolean;
  suffix?: string;
}

export function SellingField({
  name,
  label,
  categories = [],
  required,
  helper,
  placeholder,
  maxLength,
  multiline,
  numeric,
  money,
  disabled,
  suffix,
}: FieldProps) {
  const { control, getValues } = useFormContext<SellingValues>();
  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate: () => validateField(name, getValues(), categories) }}
      render={({ field: { ref, ...field }, fieldState }) => (
        <TextField
          {...field}
          inputRef={ref}
          id={`sell-${name}`}
          label={label}
          fullWidth
          required={required}
          placeholder={placeholder}
          disabled={disabled}
          multiline={multiline}
          minRows={multiline ? 3 : undefined}
          error={!!fieldState.error}
          helperText={fieldState.error?.message || helper}
          onBlur={() => {
            if (money) {
              const cents = parsePriceCents(field.value, name === "shipping");
              if (cents !== null) field.onChange((cents / 100).toFixed(2));
            }
            field.onBlur();
          }}
          inputProps={{
            maxLength,
            inputMode: money ? "decimal" : numeric ? "decimal" : undefined,
          }}
          InputProps={{
            startAdornment: money ? (
              <InputAdornment position="start">$</InputAdornment>
            ) : undefined,
            endAdornment: suffix ? (
              <InputAdornment position="end">{suffix}</InputAdornment>
            ) : undefined,
          }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 3 },
            "& input, & textarea": { fontSize: 16 },
          }}
        />
      )}
    />
  );
}

export function ChoiceField({
  name,
  label,
  options,
  onChange,
}: {
  name: "gender" | "condition" | "returns";
  label: string;
  options: { value: string; label: string; detail?: string }[];
  onChange?: (value: string) => void;
}) {
  const { control, getValues } = useFormContext<SellingValues>();
  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate: () => validateField(name, getValues(), []) }}
      render={({ field: { ref, ...field }, fieldState }) => (
        <FormControl required error={!!fieldState.error} fullWidth>
          <FormLabel
            id={`sell-${name}-label`}
            sx={{ mb: 1, color: "secondary.main", fontWeight: 600 }}
          >
            {label}
          </FormLabel>
          <RadioGroup
            {...field}
            aria-labelledby={`sell-${name}-label`}
            onChange={(event) => {
              field.onChange(event);
              onChange?.(event.target.value);
            }}
          >
            <Stack
              direction={
                name === "returns" ? "column" : { xs: "column", sm: "row" }
              }
              spacing={1}
            >
              {options.map((option, index) => (
                <Box
                  key={option.value}
                  sx={{
                    flex: 1,
                    border: "1px solid",
                    borderColor:
                      field.value === option.value ? "#ba5425" : "divider",
                    bgcolor: field.value === option.value ? "#fff6ef" : "white",
                    borderRadius: 3,
                  }}
                >
                  <FormControlLabel
                    value={option.value}
                    control={
                      <Radio
                        inputRef={index === 0 ? ref : undefined}
                        sx={{
                          color: "#a94d24",
                          "&.Mui-checked": { color: "#a94d24" },
                        }}
                      />
                    }
                    sx={{
                      m: 0,
                      width: "100%",
                      p: 0.5,
                      alignItems: option.detail ? "flex-start" : "center",
                    }}
                    label={
                      <Box sx={{ py: option.detail ? 0.6 : 0 }}>
                        <Typography fontWeight={600}>{option.label}</Typography>
                        {option.detail && (
                          <Typography variant="body2" color="text.secondary">
                            {option.detail}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </Box>
              ))}
            </Stack>
          </RadioGroup>
          {fieldState.error && (
            <FormHelperText>{fieldState.error.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

export function StepHeading({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="h5"
        component="h2"
        fontWeight={750}
        color="secondary.main"
      >
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.75 }}>
        {children}
      </Typography>
    </Box>
  );
}
